import React, { useMemo, useState } from 'react'
import {
  Paragraph,
  DangerButton,
  DialogOverlay,
  DialogOverlayActions,
  Button,
  useOverlay,
  useStatusOverlay,
} from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useFeatureFlag } from '@fs/zion-flags'
import { REMOVE_ELEMENT, saveSLSActions, UNDO } from '@fs/zion-sls'
import User from './User'

const opMap = {
  SET_PERMISSION_INFO: 'Permission',
  CREATE_ELEMENT: 'Create',
}

const actionSpanCss = css`
  display: inline-flex;
  justify-content: space-between;
  width: calc(100% - 2ch);
`

export default function ActionAndElementDetails({ selectedActions, rawData, groupedActions, fsUserAgentChain }) {
  const uniqueUserIds = useMemo(() => {
    if (!selectedActions) return []
    const userIds = new Set(selectedActions.map((action) => action?.journalAttribution?.userId).filter(Boolean))
    return Array.from(userIds)
  }, [selectedActions])
  const confirmActionsOverlay = useOverlay()

  const slsActionsSplunkDebugger = useFeatureFlag('rip_sharedImageViewer_sls_actions_splunk_debugger')

  const [actionsToConfirm, setActionsToConfirm] = useState([])
  function confirmActions(actions) {
    setActionsToConfirm(actions)
    confirmActionsOverlay.open()
  }

  const showStatusOverlay = useStatusOverlay()
  const [saving, setSaving] = useState()

  function handleRes(res) {
    const status = parseInt(res?.status, 10)
    showStatusOverlay({
      message: `Response code ${res?.status}`,
      type: status >= 200 && status < 300 ? 'success' : 'error',
    })
  }

  return (
    <>
      <details open>
        <summary>Actions</summary>
        {selectedActions && (
          <>
            <details style={{ paddingInlineStart: 12 }}>
              <summary title="May Exclude FamilySearch">Contributors to this element ({uniqueUserIds?.length})</summary>
              <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                {uniqueUserIds.map((userId) => (
                  <User key={userId} userId={userId} />
                ))}
              </div>
              {!uniqueUserIds?.length && <User fallBackToFs />}
            </details>
            <details>
              <summary style={{ paddingInlineStart: 12 }}>
                Raw Actions (<span title="Number of actions for this element only">{selectedActions?.length || 0}</span>
                )
              </summary>
              {selectedActions?.map((action) => (
                <details key={action.actionId}>
                  <summary
                    style={{ paddingInlineStart: 24 }}
                    title={new Date(action?.journalAttribution?.timestamp).toString().substring(0, 21)}
                  >
                    <span css={actionSpanCss}>
                      {' '}
                      <span>
                        {new Date(action?.journalAttribution?.timestamp).toString().substring(4, 15)}{' '}
                        <small>
                          {opMap[action?.opCode] ||
                            action?.opCode
                              .replace(/_/g, ' ')
                              .toLowerCase()
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                        </small>
                      </span>
                      <User userId={action?.journalAttribution?.userId} fallBackToFs />
                    </span>
                  </summary>
                  <div style={{ background: 'whitesmoke' }}>
                    <JsonViewer json={action} />
                    <DangerButton
                      onClick={() => confirmActions([UNDO({ id: action.actionId, partitionKey: action.partitionKey })])}
                      title="With great power comes great responsibility"
                    >
                      Undo Action
                    </DangerButton>
                  </div>
                </details>
              ))}
            </details>
            <details>
              <summary style={{ paddingInlineStart: 12 }}>
                Transactions (
                <span title="Number of grouped transactions (groups of action payloads)">
                  {Object.values(groupedActions)?.length || 0} Groups
                </span>
                –
                <span title="Number of actions across all grouped transactions (total of all actions across all payloads containing an action for this element)">
                  {Object.values(groupedActions)?.flat()?.length || 0} Actions
                </span>
                )
              </summary>
              {Object.entries(groupedActions)?.map(([transactionId, transactionActions], i) => (
                <details key={transactionId}>
                  <summary style={{ paddingInlineStart: 24 }}>
                    <span
                      css={actionSpanCss}
                      title={`Group ${i + 1}–${new Date(transactionActions?.[0]?.journalAttribution?.timestamp)
                        .toString()
                        .substring(0, 21)}`}
                    >
                      {' '}
                      <span>
                        {new Date(transactionActions?.[0]?.journalAttribution?.timestamp).toString().substring(4, 15)}{' '}
                        <small>({transactionActions?.length})</small>
                      </span>
                      <User userId={transactionActions?.[0]?.journalAttribution?.userId} fallBackToFs />
                    </span>
                  </summary>
                  <div style={{ background: 'whitesmoke' }}>
                    <JsonViewer json={transactionActions} />
                    <DangerButton
                      onClick={() =>
                        confirmActions(
                          transactionActions
                            .map((a) => ({
                              id: a.actionId,
                              partitionKey: a.partitionKey,
                              subPartitionKey: a?.referenceArg?.partitionKey,
                            }))
                            .map(UNDO)
                        )
                      }
                    >
                      Undo Transaction
                    </DangerButton>
                  </div>
                </details>
              ))}
            </details>
          </>
        )}
        {!selectedActions && <JsonViewer />}
      </details>
      <details open>
        <summary>Element</summary>
        <JsonViewer json={rawData} />
        {rawData && (
          <DangerButton
            onClick={() => {
              confirmActions([
                REMOVE_ELEMENT({
                  id: rawData.id,
                  partitionKey: rawData.partitionKey,
                  elementType: rawData.elementType,
                }),
              ])
            }}
          >
            Remove Element
          </DangerButton>
        )}
      </details>
      <DialogOverlay
        {...confirmActionsOverlay}
        title="Are You Sure?"
        footer={
          <DialogOverlayActions>
            <Button onClick={confirmActionsOverlay.close}>Cancel</Button>
            <DangerButton
              onClick={() => {
                setSaving(true)
                saveSLSActions({
                  actions: actionsToConfirm,
                  appName: 'ELITE_DATUM',
                  fsUserAgentChain,
                  splunkDebugModeArguments: slsActionsSplunkDebugger.isOn
                    ? { theFileFromWhichSaveWasCalled: 'ActionAndElementDetails' }
                    : undefined,
                })
                  .then(handleRes)
                  .catch((err) => handleRes(err.response))
                  .finally(() => {
                    setSaving(false)
                    confirmActionsOverlay.close()
                  })
              }}
              disabled={saving}
            >
              Do It
            </DangerButton>
          </DialogOverlayActions>
        }
      >
        <Paragraph size="sm">
          &quot;When you look at the dark side, careful you must be. For the dark side looks back.&quot; - Yoda
        </Paragraph>
      </DialogOverlay>
    </>
  )
}

function JsonViewer({ json, name = 'element' }) {
  return (
    <>
      {json && <pre style={{ fontSize: 12, lineHeight: 1.33 }}>{JSON.stringify(json, null, 2)}</pre>}
      {!json && (
        <Paragraph secondary size="sm">
          Select a{/^[aeiou]/.test(name) ? 'n' : ''} {name} to view its raw data
        </Paragraph>
      )}
    </>
  )
}
